import type { Href } from "expo-router";
import { COLORS } from "../../lib/brand";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, Text, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import {
  listMyAnnouncements,
  markAnnouncementViewed,
  type AnnouncementAudienceKind,
  type MyAnnouncement,
} from "../../lib/announcements";
import { useAuth } from "../../lib/authContext";

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const SAND_DARK = "#D9D2C5";
const TEXT_MUTED = "rgba(28,43,26,0.62)";

const pillWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

function audienceLabel(
  kind: AnnouncementAudienceKind,
  role: string | null,
  competitionId: string | null
): string {
  if (kind === "all") return "All users";
  if (kind === "role") return `Audience · ${role ?? "role"}`;
  if (kind === "competition") {
    return `Cohort${competitionId ? ` · ${competitionId.slice(0, 8)}` : ""}`;
  }
  return kind;
}

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function AnnouncementDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const [item, setItem] = useState<MyAnnouncement | null | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading || !user || !id) return;

    let cancelled = false;
    void (async () => {
      setErr(null);
      try {
        await markAnnouncementViewed(String(id));
        const list = await listMyAnnouncements();
        if (cancelled) return;
        setItem(list.find((a) => a.id === id) ?? null);
      } catch (e) {
        if (!cancelled) {
          setErr(e instanceof Error ? e.message : "Could not load announcement");
          setItem(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, user, id]);

  useEffect(() => {
    if (!authLoading && !user) {
      router.replace("/login" as Href);
    }
  }, [authLoading, user]);

  if (authLoading || item === undefined) {
    return (
      <SafeAreaView className="flex-1 bg-sand" edges={["top", "left", "right"]}>
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color={FOREST} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1 bg-sand" edges={["top", "left", "right"]}>
      <ScrollView className="flex-1" contentContainerStyle={{ paddingBottom: 64 }}>
        {/* Back link */}
        <View className="px-5 pt-4 md:px-11">
          <Pressable
            onPress={() => {
              if (router.canGoBack()) router.back();
              else router.replace("/notifications" as Href);
            }}
            style={pillWeb}
            accessibilityRole="link"
          >
            <Text
              className="text-[11px] uppercase"
              style={{ fontFamily: "DMSans_500Medium", color: TEXT_MUTED, letterSpacing: 1.4 }}
            >
              ← Back to inbox
            </Text>
          </Pressable>
        </View>

        {err ? (
          <View className="mx-auto w-full max-w-[760px] px-5 pt-5 md:px-11">
            <Text className="text-sm text-error" style={{ fontFamily: "DMSans_400Regular" }}>
              {err}
            </Text>
          </View>
        ) : !item ? (
          <View className="flex-1 items-center justify-center px-6 pt-20">
            <Text
              className="text-center text-[14px]"
              style={{ fontFamily: "DMSans_400Regular", color: TEXT_MUTED }}
            >
              This announcement is not available.
            </Text>
          </View>
        ) : (
          <>
            {/* Hero */}
            <View className="border-b border-sand-dark bg-linen px-5 pb-8 pt-6 md:px-11">
              <View className="mx-auto w-full max-w-[760px]">
                <View className="flex-row items-center gap-2">
                  <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
                  <Text
                    className="text-[10px] font-bold uppercase tracking-widest-3"
                    style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 2 }}
                  >
                    Announcement
                  </Text>
                </View>
                <Text
                  className="mt-3 text-[34px] leading-tight text-forest"
                  style={{ fontFamily: "CormorantGaramond_500Medium" }}
                >
                  {item.title}
                </Text>
                <View className="mt-3 flex-row flex-wrap items-center gap-2">
                  <View
                    style={{
                      paddingHorizontal: 11,
                      paddingVertical: 4,
                      borderRadius: 999,
                      borderWidth: 1,
                      borderColor: SAND_DARK,
                      backgroundColor: "rgba(28,43,26,0.04)",
                    }}
                  >
                    <Text
                      className="text-[9.5px] uppercase"
                      style={{
                        fontFamily: "DMSans_700Bold",
                        letterSpacing: 1.4,
                        color: TEXT_MUTED,
                      }}
                    >
                      {audienceLabel(item.audience_kind, item.audience_role, item.audience_competition_id)}
                    </Text>
                  </View>
                  <Text
                    className="text-[12px]"
                    style={{ fontFamily: "DMSans_400Regular", color: TEXT_MUTED }}
                  >
                    {formatWhen(item.published_at ?? item.created_at)}
                  </Text>
                </View>
              </View>
            </View>

            {/* Body */}
            <View className="mx-auto w-full max-w-[760px] px-5 pt-7 md:px-11">
              {item.body ? (
                <Text
                  className="text-[17px] leading-7 text-forest"
                  style={{ fontFamily: "CormorantGaramond_400Regular" }}
                >
                  {item.body}
                </Text>
              ) : (
                <Text
                  className="text-[13px] italic"
                  style={{ fontFamily: "DMSans_400Regular", color: TEXT_MUTED }}
                >
                  No additional message body.
                </Text>
              )}
            </View>
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
